import { useMemo, useRef, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, CalendarPlus, DoorOpen, Hourglass, UserCheck, UserPlus, Users } from "lucide-react";

import { usePageBack } from "../../context/PageBackContext.js";
import { cn } from "../../lib/utils.js";
import { DropdownButton, DropdownCheckboxRow, DropdownMultiSelect, DropdownPanel, DropdownSelect, useCloseOnOutsideClick } from "../../components/DropdownButton.js";
import { MenuList } from "../../components/ui/menu-list.js";
import { SearchInput } from "../../components/ui/search-input.js";
import { Badge, type BadgeProps } from "../../components/ui/badge.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table.js";
import type { PaginationMeta } from "../../components/ui/pagination.js";
import {
  ReportPage, FilterBar, ErrorBanner, OrgFilters, ExportButton, CardGrid, ReportStatCard, ReportTableFrame, ReportFooter, PageSizeSelect,
  STATUS_GROUP_OPTIONS, TableScrollStart, TableToolbar, dropdownProps, splitList, joinList, type OpenState,
} from "../../components/reports/ReportKit.js";
import { EmployeeHistoryDialog } from "../../components/reports/EmployeeHistoryDialog.js";
import {
  useUrlFilters, useDebouncedSearch, useReport, useOrgMeta, toParams, downloadReport,
} from "../../components/reports/useReport.js";

interface Column { key: string; label: string; group: string; default: boolean }
interface Row { id: string; status: string; cells: Record<string, string | number | null> }
interface EmployeeReport {
  columns: Column[];
  rows: Row[];
  /** Loaded by a separate request (`part=cards`), so a page turn never refetches it. */
  cards?: { total: number; active: number; onNotice: number; exited: number; joinedThisMonth: number; onboarding: number };
  meta: PaginationMeta;
}

const DEFAULTS = {
  // Opens on everyone, active and exited; the Employees filter narrows it.
  search: "", officeIds: "", departmentIds: "", statusGroup: "all", statuses: "", employeeTypes: "",
  joined: "", exited: "", sortBy: "name", sortDir: "asc", page: "1", limit: "25",
};
// The cards count across every status and join / exit date; these narrow only the table.
const ROW_ONLY = ["statusGroup", "statuses", "joinedFrom", "joinedTo", "exitedFrom", "exitedTo"];

const API = "/reports/hr/employees";
const COLS_KEY = "hrms_report_employee_columns";

const STATUS_OPTIONS = ["ONBOARDING", "ACTIVE", "ON_LEAVE", "NOTICE_PERIOD", "TERMINATED", "CONTRACTOR", "INTERN"]
  .map((s) => ({ value: s, label: s.charAt(0) + s.slice(1).toLowerCase().replace("_", " ") }));
const TYPE_OPTIONS = ["FULL_TIME", "PART_TIME", "CONTRACTOR", "INTERN"]
  .map((s) => ({ value: s, label: s.charAt(0) + s.slice(1).toLowerCase().replace("_", " ") }));

/** Columns that sort server-side, keyed by column key → API sortBy value. */
const SORTABLE: Record<string, string> = {
  name: "name", employeeCode: "code", hireDate: "hireDate", department: "department", status: "status",
};

const STATUS_TONE: Record<string, BadgeProps["tone"]> = {
  Active: "success", "On Leave": "info", "Notice Period": "warning", Terminated: "error", Onboarding: "accent",
};

/** One-click date filters for joining and leaving, instead of from/to date pickers. */
const PERIODS = [
  { value: "this-month", label: "this month" },
  { value: "last-month", label: "last month" },
  { value: "last-3-months", label: "in the last 3 months" },
  { value: "last-6-months", label: "in the last 6 months" },
  { value: "this-year", label: "this year" },
  { value: "last-year", label: "last year" },
];
const periodOptions = (verb: string) => PERIODS.map((p) => ({ value: p.value, label: `${verb} ${p.label}` }));

function periodRange(key: string): { from: string; to: string } | null {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const today = iso(now);
  switch (key) {
    case "this-month": return { from: iso(new Date(y, m, 1)), to: today };
    case "last-month": return { from: iso(new Date(y, m - 1, 1)), to: iso(new Date(y, m, 0)) };
    case "last-3-months": return { from: iso(new Date(y, m - 3, now.getDate())), to: today };
    case "last-6-months": return { from: iso(new Date(y, m - 6, now.getDate())), to: today };
    case "this-year": return { from: `${y}-01-01`, to: today };
    case "last-year": return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
    default: return null;
  }
}

/** Name and code always lead the table, whatever the picker says. */
const LEAD = ["name", "employeeCode"];

function loadPicked(): string[] | null {
  try {
    const v = JSON.parse(localStorage.getItem(COLS_KEY) ?? "null") as unknown;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : null;
  } catch { return null; }
}

export default function EmployeeReportPage() {
  usePageBack("/dashboard");
  const [f, setF] = useUrlFilters(DEFAULTS);
  const [search, setSearch] = useDebouncedSearch(f.search, (v) => setF({ search: v }));
  const [open, setOpen] = useState<string | null>(null);
  const openState: OpenState = { open, setOpen };
  const org = useOrgMeta();
  const [historyId, setHistoryId] = useState<string | null>(null);
  const [picked, setPicked] = useState<string[] | null>(loadPicked);
  const colPopRef = useRef<HTMLDivElement>(null);
  useCloseOnOutsideClick(colPopRef, open === "columns", () => setOpen((o) => (o === "columns" ? null : o)));

  const params = useMemo(() => {
    const { joined, exited, ...rest } = f;
    const j = periodRange(joined);
    const x = f.statusGroup !== "current" ? periodRange(exited) : null;
    return toParams(rest, {
      ...(j ? { joinedFrom: j.from, joinedTo: j.to } : {}),
      ...(x ? { exitedFrom: x.from, exitedTo: x.to } : {}),
    });
  }, [f]);
  const { data, cards: c, loading, error } = useReport<EmployeeReport>(API, params, "EmployeeReportPage", ROW_ONLY);

  const allColumns = data?.columns ?? [];
  const visible = useMemo(() => {
    const chosen = new Set(picked ?? allColumns.filter((c) => c.default).map((c) => c.key));
    const lead = LEAD.map((k) => allColumns.find((c) => c.key === k)).filter((c): c is Column => !!c);
    return [...lead, ...allColumns.filter((c) => !LEAD.includes(c.key) && chosen.has(c.key))];
  }, [allColumns, picked]);

  const groups = useMemo(() => {
    const out = new Map<string, Column[]>();
    for (const c of allColumns) {
      if (LEAD.includes(c.key)) continue;
      out.set(c.group, [...(out.get(c.group) ?? []), c]);
    }
    return [...out.entries()];
  }, [allColumns]);

  function toggleColumn(key: string) {
    const current = new Set(visible.map((c) => c.key));
    if (current.has(key)) current.delete(key); else current.add(key);
    const next = [...current].filter((k) => !LEAD.includes(k));
    setPicked(next);
    try { localStorage.setItem(COLS_KEY, JSON.stringify(next)); } catch { /* storage unavailable */ }
  }

  function sortBy(key: string) {
    const by = SORTABLE[key];
    if (!by) return;
    setF(f.sortBy === by ? { sortDir: f.sortDir === "asc" ? "desc" : "asc" } : { sortBy: by, sortDir: "asc" });
  }

  const rows = data?.rows ?? [];
  const exportParams = useMemo(() => {
    const p = new URLSearchParams(params);
    p.set("columns", visible.map((col) => col.key).join(","));
    return p;
  }, [params, visible]);

  return (
    <ReportPage
      title="Employee Reports"
      description="Every employee field in one table — current and exited staff, with each person's change history."
    >
      <CardGrid>
        <ReportStatCard icon={Users} tone="brand" label="Employees" value={c?.total ?? "—"} caption="In scope, all statuses" />
        <ReportStatCard icon={UserCheck} tone="success" label="Active" value={c?.active ?? "—"} caption="Incl. on leave" />
        <ReportStatCard icon={UserPlus} tone="accent" label="Onboarding" value={c?.onboarding ?? "—"} />
        <ReportStatCard icon={Hourglass} tone="warning" label="Notice Period" value={c?.onNotice ?? "—"} />
        <ReportStatCard icon={DoorOpen} tone="danger" label="Exited" value={c?.exited ?? "—"} />
        <ReportStatCard icon={CalendarPlus} tone="cyan" label="Joined This Month" value={c?.joinedThisMonth ?? "—"} />
      </CardGrid>

      <FilterBar>
        <SearchInput className="w-60" placeholder="Search name, code or email" value={search} onChange={(e) => setSearch(e.target.value)} />
        <OrgFilters meta={org} officeIds={f.officeIds} departmentIds={f.departmentIds} onChange={setF} openState={openState} />
        <DropdownSelect
          value={f.statusGroup} onChange={(v) => setF({ statusGroup: v, statuses: "" })} options={STATUS_GROUP_OPTIONS}
          placeholder="All employees" ariaLabel="Employees" clearable={false} {...dropdownProps("group", openState)}
        />
        <DropdownMultiSelect
          values={splitList(f.statuses)} onChange={(v) => setF({ statuses: joinList(v) })} options={STATUS_OPTIONS}
          allLabel="Any status" ariaLabel="Status" {...dropdownProps("status", openState)}
        />
        <DropdownMultiSelect
          values={splitList(f.employeeTypes)} onChange={(v) => setF({ employeeTypes: joinList(v) })} options={TYPE_OPTIONS}
          allLabel="All types" ariaLabel="Employee type" {...dropdownProps("type", openState)}
        />
        <DropdownSelect
          value={f.joined} onChange={(v) => setF({ joined: v })} options={periodOptions("Joined")}
          placeholder="Joined: any time" ariaLabel="Joined" {...dropdownProps("joined", openState)}
        />
        {f.statusGroup !== "current" && (
          <DropdownSelect
            value={f.exited} onChange={(v) => setF({ exited: v })} options={periodOptions("Left")}
            placeholder="Left: any time" ariaLabel="Last working day" {...dropdownProps("exited", openState)}
          />
        )}
      </FilterBar>

      <TableToolbar>
        <div ref={colPopRef} className="relative shrink-0">
          <DropdownButton expanded={open === "columns"} onClick={() => setOpen((o) => (o === "columns" ? null : "columns"))}>
            Columns ({visible.length})
          </DropdownButton>
          {open === "columns" && (
            <DropdownPanel align="end" className="max-h-96 min-w-[220px] overflow-y-auto">
              <MenuList className="p-2">
                {groups.map(([group, cols]) => (
                  <div key={group} className="mb-1">
                    <div className="px-1.5 pt-1 text-[10px] font-bold uppercase tracking-[0.5px] text-surface-500">{group}</div>
                    {cols.map((col) => (
                      <DropdownCheckboxRow key={col.key} checked={visible.some((v) => v.key === col.key)} onChange={() => toggleColumn(col.key)}>
                        {col.label}
                      </DropdownCheckboxRow>
                    ))}
                  </div>
                ))}
              </MenuList>
            </DropdownPanel>
          )}
        </div>
        <PageSizeSelect limit={f.limit} onLimit={(v) => setF({ limit: v })} openState={openState} />
        <ExportButton onExport={() => downloadReport(API, exportParams, "employee-report.xlsx")} />
      </TableToolbar>

      <ErrorBanner message={error} />

      <ReportTableFrame loading={loading} empty={rows.length === 0} emptyText="No employees match these filters." contentKey={visible.map((v) => v.key).join(",")}>
        <Table className="w-max min-w-full">
          <TableHeader>
            <TableRow className="bg-surface-50">
              {visible.map((col, i) => {
                const sortable = !!SORTABLE[col.key];
                const active = SORTABLE[col.key] === f.sortBy;
                const Arrow = !active ? ArrowUpDown : f.sortDir === "asc" ? ArrowUp : ArrowDown;
                return (
                  <TableHead
                    key={col.key}
                    className={cn("whitespace-nowrap", i === 0 && "sticky left-0 z-10 bg-surface-50", sortable && "cursor-pointer select-none hover:text-surface-800")}
                    onClick={sortable ? () => sortBy(col.key) : undefined}
                    aria-sort={active ? (f.sortDir === "asc" ? "ascending" : "descending") : undefined}
                  >
                    {i === 0 && <TableScrollStart />}
                    <span className="inline-flex items-center gap-1">
                      {col.label}{sortable && <Arrow size={11} className={active ? "text-brand-600" : "text-surface-300"} />}
                    </span>
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} className="group cursor-pointer hover:bg-surface-50" onClick={() => setHistoryId(r.id)} title="Click to see this employee's history">
                {visible.map((col, i) => {
                  const v = r.cells[col.key];
                  const restricted = v === "Restricted";
                  return (
                    <TableCell
                      key={col.key}
                      className={cn("px-3 align-middle", i === 0 && "sticky left-0 z-10 bg-surface-0 font-semibold text-surface-900 group-hover:bg-surface-50")}
                    >
                      {/* Natural width up to a cap; longer text wraps inside the cell instead of
                          being cut off. A td's own max-width is only advisory, hence the div. */}
                      <div className="w-max max-w-[240px] whitespace-normal break-words">
                      {col.key === "status" && typeof v === "string" ? (
                        <Badge tone={STATUS_TONE[v] ?? "neutral"}>{v}</Badge>
                      ) : restricted ? (
                        <span className="text-[11px] italic text-surface-400">Restricted</span>
                      ) : v == null || v === "" ? (
                        <span className="text-surface-300">—</span>
                      ) : col.key === "currentCtc" && typeof v === "number" ? (
                        `₹${v.toLocaleString("en-IN")}`
                      ) : (
                        v
                      )}
                      </div>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ReportTableFrame>

      <ReportFooter
        meta={data?.meta} onPage={(p) => setF({ page: String(p) })}
      />

      <EmployeeHistoryDialog employeeId={historyId} onClose={() => setHistoryId(null)} />
    </ReportPage>
  );
}
